import DateInput, { DateInputSize } from "@galaxy-io/dls/inputs/DateInput";
import { InputSize, InputVariant } from "@galaxy-io/dls/inputs/Input";
import NumberInput from "@galaxy-io/dls/inputs/NumberInput";
import PasswordInput from "@galaxy-io/dls/inputs/PasswordInput";
import SearchInput from "@galaxy-io/dls/inputs/SearchInput";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import type { ComponentProps } from "react";

type InputProps = Omit<ComponentProps<"input">, "onChange" | "value" | "defaultValue" | "size"> & {
  label?: string;
  hideLabel?: boolean;
  size?: InputSize;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
};
/** Keep GTM's string form values at the boundary; DLS owns fields and interaction. */
export function Input({
  type,
  label,
  hideLabel = false,
  size = InputSize.MEDIUM,
  value,
  defaultValue,
  onChange,
  disabled,
  required,
  readOnly,
  min,
  max,
  step,
  "aria-label": ariaLabel,
  ...props
}: InputProps) {
  if (type === "date")
    return (
      <DateInput
        label={hideLabel ? undefined : (label ?? (type === "search" ? ariaLabel : undefined))}
        size={DateInputSize[size]}
        id={props.id}
        className={props.className}
        ariaLabel={ariaLabel ?? label}
        isRequired={required}
        placeholder={props.placeholder}
        value={value ? new Date(`${value}T00:00:00Z`) : null}
        min={min ? new Date(`${min}T00:00:00Z`) : undefined}
        max={max ? new Date(`${max}T00:00:00Z`) : undefined}
        timeZone="UTC"
        onChange={(date) => onChange?.(date?.toISOString().slice(0, 10) ?? "")}
        isDisabled={disabled}
        isReadOnly={readOnly}
        fillWidth
      />
    );
  if (type === "number")
    return (
      <NumberInput
        label={hideLabel ? undefined : (label ?? (type === "search" ? ariaLabel : undefined))}
        size={size}
        {...props}
        defaultValue={defaultValue ? Number(defaultValue) : null}
        isRequired={required}
        ariaLabel={ariaLabel ?? label}
        value={value ? Number(value) : null}
        onChange={(next) => onChange?.(next === null ? "" : String(next))}
        min={min === undefined ? undefined : Number(min)}
        max={max === undefined ? undefined : Number(max)}
        step={step === undefined ? undefined : Number(step)}
        isDisabled={disabled}
        isReadOnly={readOnly}
        fillWidth
      />
    );
  const Field = type === "password" ? PasswordInput : type === "search" ? SearchInput : TextInput;
  return (
    <Field
      label={hideLabel ? undefined : (label ?? (type === "search" ? ariaLabel : undefined))}
      size={size}
      {...props}
      ariaLabel={ariaLabel ?? label}
      inputMode={
        props.inputMode ??
        (type === "email" ? "email" : type === "url" ? "url" : type === "tel" ? "tel" : undefined)
      }
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      isDisabled={disabled}
      isRequired={required}
      isReadOnly={readOnly}
      variant={InputVariant.BASE}
      fillWidth
    />
  );
}
